import "@/App.css";
import { KEYS } from "@/lib/storage-keys";
import { Suspense, useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ThemeProvider } from "next-themes";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { DisplaySettingsProvider } from "@/context/DisplaySettingsContext";
import { LocationProvider } from "@/context/LocationContext";
import { UnsavedChangesProvider } from "@/context/UnsavedChangesContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import { lazyPage } from "@/lib/lazy-page";
import RouteSpinner from "@/components/RouteSpinner";
import PlanGate from "@/components/entitlements/PlanGate";
import { RouteErrorBoundary } from "@/components/ErrorBoundary";
import { startDashboardPrefetch, prefetchOverviewBookings } from "@/lib/startup-prefetch";
import { overviewBookingParams } from "@/lib/overview-window";
import { approvalGateStatus } from "@/lib/approval";

// Code splitting per rotta: il bundle iniziale contiene solo shell, provider e
// routing; ogni pagina si scarica quando serve. Con `webpackPrefetch` il
// browser, a pagina caricata e a riposo, scarica in anticipo le sole pagine
// d'ingresso piu' probabili (accesso e dashboard principale), cosi' il
// passaggio Landing -> Accedi -> Dashboard non attende la rete.
const DashboardLayout = lazyPage(() => import(/* webpackPrefetch: true */ "@/components/DashboardLayout"));
const AuthPage = lazyPage(() => import(/* webpackPrefetch: true */ "@/pages/Auth"));
const VerifyEmailPage = lazyPage(() => import("@/pages/VerifyEmail"));
const ForgotPasswordPage = lazyPage(() => import("@/pages/ForgotPassword"));
const ResetPasswordPage = lazyPage(() => import("@/pages/ResetPassword"));
const Landing = lazyPage(() => import("@/pages/Landing"));
const ComeFunziona = lazyPage(() => import("@/pages/ComeFunziona"));
const Prezzi = lazyPage(() => import("@/pages/Prezzi"));
const MfaPage = lazyPage(() => import("@/pages/dashboard/Mfa"));
const PrivacyPage = lazyPage(() => import("@/pages/Legal").then(m => ({ default: m.PrivacyPage })));
const CookiePage = lazyPage(() => import("@/pages/Legal").then(m => ({ default: m.CookiePage })));
const TermsPage = lazyPage(() => import("@/pages/Legal").then(m => ({ default: m.TermsPage })));
const SubprocessorsPage = lazyPage(() => import("@/pages/Legal").then(m => ({ default: m.SubprocessorsPage })));
const DpaPage = lazyPage(() => import("@/pages/Legal").then(m => ({ default: m.DpaPage })));
const Overview = lazyPage(() => import(/* webpackPrefetch: true */ "@/pages/dashboard/Overview"));
const Calendario = lazyPage(() => import(/* webpackPrefetch: true */ "@/pages/dashboard/Calendario"));
const Prenotazioni = lazyPage(() => import(/* webpackPrefetch: true */ "@/pages/dashboard/Prenotazioni"));
const Clienti = lazyPage(() => import("@/pages/dashboard/Clienti"));
const Sedi = lazyPage(() => import("@/pages/dashboard/Sedi"));
const Servizi = lazyPage(() => import("@/pages/dashboard/Servizi"));
const Risorse = lazyPage(() => import("@/pages/dashboard/Risorse"));
const Orari = lazyPage(() => import("@/pages/dashboard/Orari"));
const InfoAttivita = lazyPage(() => import("@/pages/dashboard/InfoAttivita"));
const WhatsAppPage = lazyPage(() => import("@/pages/dashboard/WhatsApp"));
const InboxPage = lazyPage(() => import("@/pages/dashboard/Inbox"));
const Impostazioni = lazyPage(() => import("@/pages/dashboard/Impostazioni"));
const Assistenza = lazyPage(() => import("@/pages/dashboard/Assistenza"));
const Admin = lazyPage(() => import("@/pages/dashboard/Admin"));
const AdminPiani = lazyPage(() => import("@/pages/dashboard/AdminPiani"));
const AdminLab = lazyPage(() => import("@/pages/dashboard/AdminLab"));
const AdminRete = lazyPage(() => import("@/pages/dashboard/AdminRete"));
const IlMioPiano = lazyPage(() => import("@/pages/dashboard/IlMioPiano"));
const ChangePassword = lazyPage(() => import("@/pages/dashboard/ChangePassword"));

function DashboardHome() {
  const { user } = useAuth();
  return user?.role === "super_admin" ? <Navigate to="/dashboard/admin" replace /> : <Overview />;
}

// [fix-sicurezza 29/09/2026] Nessun controllo di ruolo lato client sulla
// route /dashboard/admin (audit 2026-09-27, Low): il backend e' gia' protetto
// su tutti gli endpoint admin da require_super_admin, quindi non e' un data
// leak, ma un non-admin che ci navigasse vedrebbe la pagina montarsi e poi
// fallire sulle chiamate API. Difesa in profondita': redirect immediato.
function AdminRoute({ children, allowWithoutMfa = false }) {
  const { user, mfa } = useAuth();
  if (user?.role !== "super_admin") return <Navigate to="/dashboard" replace />;
  // P-20: con la MFA obbligatoria l'amministrazione si apre solo dopo averla
  // configurata (o superata): prima si va alla pagina di iscrizione.
  if (!allowWithoutMfa && mfa?.required && !mfa?.session_verified) return <Navigate to="/dashboard/mfa" replace />;
  return children;
}

// Ad ogni cambio di pagina si riparte dall'inizio (un link non deve aprire la
// nuova pagina "a metà"); i link con #ancora restano gestiti dal browser.
function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => { if (!hash) window.scrollTo(0, 0); }, [pathname, hash]);
  return null;
}

// La home pubblica non sapeva nulla della sessione: un utente già autenticato
// che digitava aiposto.it (invece di aiposto.it/dashboard) vedeva sempre la
// Landing, perché Landing.js non consulta mai useAuth(). Questo wrapper lo fa
// al posto suo, senza toccare Landing: in attesa dell'esito di /auth/me resta
// lo spinner (mai la Landing "a intermittenza"), un utente loggato va dritto
// alla dashboard, chi non è loggato vede la Landing come sempre.
function HomeRoute() {
  const { user, loading } = useAuth();
  if (loading) return <RouteSpinner />;
  if (user) return <Navigate to="/dashboard" replace />;
  return <Landing />;
}

function AppRouter() {
  return (
    <>
    <ScrollToTop />
    <RouteErrorBoundary>
    <Suspense fallback={<RouteSpinner />}>
    <Routes>
      <Route path="/" element={<HomeRoute />} />
      <Route path="/come-funziona" element={<ComeFunziona />} />
      <Route path="/prezzi" element={<Prezzi />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/cookie" element={<CookiePage />} />
      <Route path="/termini" element={<TermsPage />} />
      <Route path="/dpa" element={<DpaPage />} />
      <Route path="/sub-responsabili" element={<SubprocessorsPage />} />
      <Route path="/auth" element={<AuthPage />} />
      <Route path="/verifica-email" element={<VerifyEmailPage />} />
      <Route path="/password-dimenticata" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      {/* Configurazione guidata eliminata: i vecchi link portano alla dashboard. */}
      <Route path="/onboarding" element={<Navigate to="/dashboard" replace />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<DashboardHome />} />
        <Route path="admin" element={<AdminRoute><Admin /></AdminRoute>} />
        <Route path="admin/piani" element={<AdminRoute><AdminPiani /></AdminRoute>} />
        <Route path="admin/laboratorio" element={<AdminRoute><AdminLab /></AdminRoute>} />
        <Route path="admin/rete-commerciale" element={<AdminRoute><AdminRete /></AdminRoute>} />
        <Route path="piano" element={<IlMioPiano />} />
        <Route path="mfa" element={<AdminRoute allowWithoutMfa><MfaPage /></AdminRoute>} />
        <Route path="change-password" element={<ChangePassword />} />
        <Route path="calendario" element={<PlanGate feature="calendar_view"><Calendario /></PlanGate>} />
        <Route path="prenotazioni" element={<Prenotazioni />} />
        <Route path="clienti" element={<Clienti />} />
        <Route path="customers" element={<Navigate to="/dashboard/clienti" replace />} />
        <Route path="sedi" element={<Sedi />} />
        <Route path="servizi" element={<Servizi />} />
        <Route path="risorse" element={<Risorse />} />
        <Route path="orari" element={<Orari />} />
        <Route path="info-attivita" element={<InfoAttivita />} />
        <Route path="whatsapp" element={<WhatsAppPage />} />
        <Route path="inbox" element={<InboxPage />} />
        <Route path="impostazioni" element={<Impostazioni />} />
        <Route path="assistenza" element={<Assistenza />} />
      </Route>
    </Routes>
    </Suspense>
    </RouteErrorBoundary>
    </>
  );
}

// [merge-adapter: P8 perf] Panoramica, Calendario e Prenotazioni chiedevano
// ciascuna per conto proprio, in modo identico, /resources e /services ad
// ogni montaggio — stessi dati, tre round-trip separati navigando tra le tre
// pagine nella stessa sessione. Una cache condivisa (staleTime 60s: dati di
// configurazione che cambiano raramente, non prenotazioni) evita il refetch
// ridondante senza toccare come le prenotazioni stesse vengono caricate
// (restano axios dirette, gia' sistemate per P3/P4).
const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 60_000 } },
});
// Esportata solo perché i test possano svuotare la cache tra un caso e l'altro.
export { queryClient };

// Avvio diretto su una pagina protetta (ricarica, link, segnalibro): il file del
// layout e quello della pagina verrebbero richiesti SOLO dopo /auth/me (il
// ProtectedRoute non renderizza i figli finche' la sessione non e' nota). Si
// scaricano invece subito, in parallelo, insieme alle sedi e (per le pagine che
// li usano) al catalogo risorse/servizi. Vedi lib/startup-prefetch.js.
const DASHBOARD_PAGES = {
  calendario: { page: Calendario },
  prenotazioni: { page: Prenotazioni, catalog: true },
  sedi: { page: Sedi },
  servizi: { page: Servizi },
  risorse: { page: Risorse },
  orari: { page: Orari },
  "info-attivita": { page: InfoAttivita },
  whatsapp: { page: WhatsAppPage },
  inbox: { page: InboxPage },
  impostazioni: { page: Impostazioni },
  assistenza: { page: Assistenza },
  admin: { page: Admin },
  piano: { page: IlMioPiano },
  mfa: { page: MfaPage },
  "change-password": { page: ChangePassword },
};

export function startupRouteFor(pathname) {
  const parts = String(pathname || "").split("/").filter(Boolean);
  if (parts[0] !== "dashboard") return null;
  if (parts.length === 1) return { pages: [DashboardLayout, Overview], catalog: true };
  const entry = DASHBOARD_PAGES[parts[1]];
  return { pages: entry ? [DashboardLayout, entry.page] : [DashboardLayout], catalog: !!entry?.catalog };
}

const INITIAL_PATH = typeof window !== "undefined" ? window.location.pathname : "";
if (typeof window !== "undefined") startDashboardPrefetch(queryClient, startupRouteFor(INITIAL_PATH));

// Apertura diretta della Panoramica: appena /auth/me dice chi e' l'utente e che
// fuso ha l'attivita', parte la richiesta delle prenotazioni della finestra
// "oggi + 6 giorni", mentre la pagina si sta ancora scaricando. Solo dove la
// Panoramica verra' davvero mostrata (non admin, email verificata, attivita' non in attesa
// di approvazione).
function DashboardDataPrefetch() {
  const { user, business, approval } = useAuth();
  const done = useRef(false);
  useEffect(() => {
    if (done.current || !user) return;
    done.current = true;
    const overviewShown = /^\/dashboard\/?$/.test(INITIAL_PATH)
      && user.role !== "super_admin"
      && user.email_verified !== false
      && !approvalGateStatus(user, approval);
    if (overviewShown && business) prefetchOverviewBookings(overviewBookingParams(business.timezone || "Europe/Rome"));
  }, [user, business, approval]);
  return null;
}

function App() {
  return (
    <div className="App">
      <QueryClientProvider client={queryClient}>
        <ThemeProvider attribute="class" defaultTheme="system" enableSystem storageKey={KEYS.theme}>
          <DisplaySettingsProvider>
            <BrowserRouter>
              <AuthProvider>
                <LocationProvider>
                  <DashboardDataPrefetch />
                  <UnsavedChangesProvider>
                    <AppRouter />
                  </UnsavedChangesProvider>
                  <Toaster position="top-right" richColors />
                </LocationProvider>
              </AuthProvider>
            </BrowserRouter>
          </DisplaySettingsProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </div>
  );
}

export default App;
